import { authService } from "./authService";
import { functionsService } from "./functionsService";
import type { User } from "firebase/auth";
import { racerDataCache } from "./racerDataCache";

export type CurrentViewerRaceAccessState = {
  canAccessTestRaces: boolean;
  isAdmin: boolean;
};

const deniedAccess = (): CurrentViewerRaceAccessState => ({
  canAccessTestRaces: false,
  isAdmin: false,
});

let accessGeneration = 0;
let accessRequestSequence = 0;
let latestCredentialVersion = 0;
let latestCredential: {
  user: User;
  token: string;
  issuedAtMillis: number;
  expiresAtMillis: number;
  requestSequence: number;
} | null = null;
let pendingAccess: {
  user: User;
  token: string | null;
  generation: number;
  credentialVersion: number;
  promise: Promise<CurrentViewerRaceAccessState>;
} | null = null;

// Only concurrent work is shared. A later request must recheck the allowlist.
export const invalidateCurrentViewerRaceAccess = (): void => {
  racerDataCache.clear();
  accessGeneration += 1;
  pendingAccess = null;
  latestCredential = null;
  latestCredentialVersion += 1;
};

export const claimIncludesAdminRole = (claims: Record<string, unknown>): boolean => {
  if (claims.admin === true) {
    return true;
  }

  if (claims.role === "admin") {
    return true;
  }

  if (Array.isArray(claims.roles)) {
    return claims.roles.some((role) => role === "admin");
  }

  return false;
};

export const getCurrentViewerRaceAccessState =
  async (options: { throwOnFailure?: boolean } = {}): Promise<CurrentViewerRaceAccessState> => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      return deniedAccess();
    }

    const generation = accessGeneration;
    const requestSequence = ++accessRequestSequence;
    let token: string | null = null;
    let credentialVersion: number | null = null;
    const isCurrentViewer = () =>
      generation === accessGeneration && authService.getCurrentUser() === currentUser &&
      (credentialVersion === null || credentialVersion === latestCredentialVersion) &&
      (token === null || (latestCredential?.user === currentUser && latestCredential.token === token));

    try {
      const tokenResult = await currentUser.getIdTokenResult();
      if (!isCurrentViewer()) return deniedAccess();
      const issuedAtMillis = Date.parse(tokenResult.issuedAtTime) || 0;
      const expiresAtMillis = Date.parse(tokenResult.expirationTime) || 0;
      // Token reads can settle out of order during refresh. A delayed old token
      // must not replace the credential used by a newer access check.
      if (latestCredential?.user === currentUser && latestCredential.token !== tokenResult.token) {
        const isOlderToken = issuedAtMillis < latestCredential.issuedAtMillis ||
          (issuedAtMillis === latestCredential.issuedAtMillis &&
            (expiresAtMillis < latestCredential.expiresAtMillis ||
              (expiresAtMillis === latestCredential.expiresAtMillis && requestSequence < latestCredential.requestSequence)));
        if (isOlderToken) return deniedAccess();
      }
      token = tokenResult.token;
      if (latestCredential?.user !== currentUser || latestCredential.token !== token) {
        latestCredentialVersion += 1;
      }
      credentialVersion = latestCredentialVersion;
      latestCredential = {
        user: currentUser, token, issuedAtMillis, expiresAtMillis,
        requestSequence: Math.max(requestSequence, latestCredential?.user === currentUser ? latestCredential.requestSequence : 0),
      };
      const claims = (tokenResult.claims || {}) as Record<string, unknown>;
      const isAdmin = claimIncludesAdminRole(claims);
      if (isAdmin) {
        return {
          canAccessTestRaces: true,
          isAdmin: true,
        };
      }
    } catch {
      // Fall through to the server-side allowlist check.
    }

    // A failed token read still belongs to the credential version observed when
    // its server fallback starts. Do not share/apply it after a credential change.
    if (credentialVersion === null) credentialVersion = latestCredentialVersion;
    if (!isCurrentViewer()) return deniedAccess();
    if (
      pendingAccess?.user === currentUser &&
      pendingAccess.token === token &&
      pendingAccess.generation === generation &&
      pendingAccess.credentialVersion === credentialVersion
    ) {
      return options.throwOnFailure ? pendingAccess.promise : pendingAccess.promise.catch(deniedAccess);
    }

    const promise = functionsService.getCurrentViewerTestRaceAccess()
      .then((result) => isCurrentViewer() ? {
        canAccessTestRaces: Boolean(result.canAccessTestRaces || result.isAdmin),
        isAdmin: Boolean(result.isAdmin),
      } : deniedAccess())
      .finally(() => {
        if (pendingAccess?.promise === promise) pendingAccess = null;
      });
    pendingAccess = { user: currentUser, token, generation, credentialVersion, promise };
    return options.throwOnFailure ? promise : promise.catch(deniedAccess);
  };
